import { useEffect, useRef } from "react";
import * as THREE from "three";

const VERT = `
uniform float uPixelRatio;
uniform float uTime;
attribute float aScale;
attribute float aPhase;
varying float vTw;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vTw = 0.65 + 0.35 * sin(uTime * 2.2 + aPhase * 40.0);
  gl_PointSize = aScale * uPixelRatio * vTw;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = `
uniform vec3 uColor;
uniform float uOpacity;
varying float vTw;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.1, d) * uOpacity * vTw;
  if (a < 0.004) discard;
  gl_FragColor = vec4(uColor, a);
}
`;

// ---- constellation pattern generators (nodes in [-1,1] space) ----
function hexMolecule() {
  const nodes = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    nodes.push([Math.cos(a) * 0.42, Math.sin(a) * 0.42]);
  }
  nodes.push([0.75, 0.28], [-0.75, -0.28]);
  const edges = [[0,1],[1,2],[2,3],[3,4],[4,5],[5,0],[0,6],[3,7]];
  return { nodes, edges };
}
function droplet() {
  const nodes = [];
  const n = 10;
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const r = 0.34 * (1 - 0.55 * Math.sin(t / 2));
    nodes.push([Math.sin(t) * r * 1.15, -Math.cos(t) * 0.42 + (t > Math.PI ? 0 : 0)]);
  }
  nodes.push([0, 0.55]);
  const edges = [];
  for (let i = 0; i < n; i++) edges.push([i, (i + 1) % n]);
  edges.push([0, n], [n - 1, n]);
  return { nodes, edges };
}
function mitochondrion() {
  const nodes = [];
  const n = 12;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    nodes.push([Math.cos(a) * 0.72, Math.sin(a) * 0.34]);
  }
  const zig = 5;
  for (let i = 0; i < zig; i++) {
    nodes.push([-0.45 + (i / (zig - 1)) * 0.9, i % 2 === 0 ? 0.14 : -0.14]);
  }
  const edges = [];
  for (let i = 0; i < n; i++) edges.push([i, (i + 1) % n]);
  for (let i = 0; i < zig - 1; i++) edges.push([n + i, n + i + 1]);
  return { nodes, edges };
}
function helix() {
  const nodes = [];
  const steps = 9;
  for (let i = 0; i < steps; i++) {
    const x = -0.8 + (i / (steps - 1)) * 1.6;
    const p = (i / (steps - 1)) * Math.PI * 2.2;
    nodes.push([x, Math.sin(p) * 0.32]);
    nodes.push([x, -Math.sin(p) * 0.32]);
  }
  const edges = [];
  for (let i = 0; i < steps - 1; i++) {
    edges.push([i * 2, (i + 1) * 2]);
    edges.push([i * 2 + 1, (i + 1) * 2 + 1]);
  }
  for (let i = 0; i < steps; i += 2) edges.push([i * 2, i * 2 + 1]);
  return { nodes, edges };
}
function neuron() {
  const nodes = [[0, 0]];
  const edges = [];
  const branches = 6;
  for (let b = 0; b < branches; b++) {
    const a = (b / branches) * Math.PI * 2 + 0.35;
    const midIdx = nodes.length;
    nodes.push([Math.cos(a) * 0.35, Math.sin(a) * 0.28]);
    edges.push([0, midIdx]);
    const tipIdx = nodes.length;
    const spread = b % 2 === 0 ? 0.25 : -0.2;
    nodes.push([Math.cos(a + spread * 0.5) * 0.72, Math.sin(a + spread * 0.5) * 0.52]);
    edges.push([midIdx, tipIdx]);
    if (b % 2 === 0) {
      const tip2 = nodes.length;
      nodes.push([Math.cos(a - 0.3) * 0.68, Math.sin(a - 0.3) * 0.48]);
      edges.push([midIdx, tip2]);
    }
  }
  return { nodes, edges };
}

const PATTERNS = [hexMolecule(), droplet(), mitochondrion(), helix(), neuron()];
const N_LINE = 200;
const N_NODE = 26;

function buildTargets(pattern, scale, yStretch, offsetX) {
  const { nodes, edges } = pattern;
  const line = new Float32Array(N_LINE * 3);
  const lens = edges.map(([a, b]) => {
    const dx = nodes[b][0] - nodes[a][0];
    const dy = nodes[b][1] - nodes[a][1];
    return Math.hypot(dx, dy);
  });
  const total = lens.reduce((s, l) => s + l, 0);
  let k = 0;
  edges.forEach(([a, b], ei) => {
    const cnt = Math.max(2, Math.round((lens[ei] / total) * N_LINE));
    for (let i = 0; i < cnt && k < N_LINE; i++, k++) {
      const t = (i + 0.5) / cnt;
      line[k * 3] = (nodes[a][0] + (nodes[b][0] - nodes[a][0]) * t) * scale + offsetX;
      line[k * 3 + 1] = (nodes[a][1] + (nodes[b][1] - nodes[a][1]) * t) * scale * yStretch;
      line[k * 3 + 2] = 0;
    }
  });
  for (; k < N_LINE; k++) {
    const src = Math.floor(Math.random() * Math.max(1, k));
    line[k * 3] = line[src * 3];
    line[k * 3 + 1] = line[src * 3 + 1];
  }
  const node = new Float32Array(N_NODE * 3);
  for (let i = 0; i < N_NODE; i++) {
    const p = nodes[i % nodes.length];
    node[i * 3] = p[0] * scale + offsetX;
    node[i * 3 + 1] = p[1] * scale * yStretch;
    node[i * 3 + 2] = 0;
  }
  return { line, node };
}

function makeCloud(count, size, opacity) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  const scales = new Float32Array(count);
  const phases = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    scales[i] = size * (0.7 + Math.random() * 0.6);
    phases[i] = Math.random();
  }
  geo.setAttribute("aScale", new THREE.BufferAttribute(scales, 1));
  geo.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(0xffd700) },
      uOpacity: { value: opacity },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  return { geo, mat, points, scatter: new Float32Array(count * 3), start: new Float32Array(count * 3) };
}

export const Constellations = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    let aspect = mount.clientWidth / mount.clientHeight;
    const camera = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, 0.1, 10);
    camera.position.z = 2;

    const scale = 0.85;
    const offsetX = aspect > 1.4 ? 1.15 : 0;
    const targets = PATTERNS.map((p) => buildTargets(p, scale, 1, offsetX));

    const lineCloud = makeCloud(N_LINE, 3.2, 0.65);
    const nodeCloud = makeCloud(N_NODE, 9.0, 1.0);
    scene.add(lineCloud.points, nodeCloud.points);

    // init at pattern 0
    lineCloud.geo.attributes.position.array.set(targets[0].line);
    nodeCloud.geo.attributes.position.array.set(targets[0].node);

    let active = 0;
    let progress = 1;

    const beginMorph = (idx) => {
      if (idx === active) return;
      active = idx;
      progress = 0;
      [lineCloud, nodeCloud].forEach((c) => {
        c.start.set(c.geo.attributes.position.array);
        for (let i = 0; i < c.scatter.length / 3; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = 0.15 + Math.random() * 0.45;
          c.scatter[i * 3] = Math.cos(a) * r;
          c.scatter[i * 3 + 1] = Math.sin(a) * r;
          c.scatter[i * 3 + 2] = 0;
        }
      });
    };

    // watch series blocks
    const blocks = Array.from(document.querySelectorAll('[data-testid^="series-"]'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const idx = blocks.indexOf(en.target);
            if (idx >= 0) beginMorph(idx);
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );
    blocks.forEach((b) => io.observe(b));

    let visible = true;
    const vio = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    vio.observe(mount);

    const clock = new THREE.Clock();
    let raf;
    const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!visible || document.hidden) return;
      const dt = Math.min(clock.getDelta(), 0.05);
      const t = clock.elapsedTime;
      if (progress < 1) progress = Math.min(progress + dt / 1.4, 1);
      const e = easeInOut(progress);
      const bell = Math.sin(Math.PI * Math.min(progress, 1));

      const tgt = targets[active];
      [[lineCloud, tgt.line], [nodeCloud, tgt.node]].forEach(([c, target]) => {
        const arr = c.geo.attributes.position.array;
        for (let i = 0; i < arr.length; i += 3) {
          const wob = Math.sin(t * 0.8 + i) * 0.006;
          arr[i] = c.start[i] + (target[i] - c.start[i]) * e + c.scatter[i] * bell + wob;
          arr[i + 1] = c.start[i + 1] + (target[i + 1] - c.start[i + 1]) * e + c.scatter[i + 1] * bell - wob;
        }
        c.geo.attributes.position.needsUpdate = true;
        c.mat.uniforms.uTime.value = t;
      });
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      aspect = w / h;
      camera.left = -aspect;
      camera.right = aspect;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      vio.disconnect();
      window.removeEventListener("resize", onResize);
      [lineCloud, nodeCloud].forEach((c) => { c.geo.dispose(); c.mat.dispose(); });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="absolute inset-0 z-0 pointer-events-none" data-testid="catalogue-constellations">
      <div ref={mountRef} className="sticky top-0 h-screen w-full opacity-80" />
    </div>
  );
};
